import { useState, useRef } from 'react';
import {
  Settings,
  PanelLeft,
  Palette,
  Globe,
  Type,
  Bell,
  Database,
  User,
  LogOut,
  Download,
  Upload,
} from 'lucide-react';
import { apiGet } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useToast } from '../contexts/ToastContext';

const TIMEZONE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect (browser)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern European (EET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

function Section({ icon: Icon, title, children }: { icon: typeof Settings; title: string; children: React.ReactNode }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-bg-secondary border-b border-border">
        <Icon size={16} className="text-accent" />
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      </div>
      <div className="px-4 py-3 space-y-4">
        {children}
      </div>
    </div>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-text-primary">{label}</div>
        {description && <div className="text-xs text-text-muted mt-0.5">{description}</div>}
      </div>
      <div className="flex-shrink-0">
        {children}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
        checked ? 'bg-accent' : 'bg-bg-hover'
      }`}
    >
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${
          checked ? 'translate-x-5' : ''
        }`}
      />
    </button>
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="px-2 py-1.5 bg-bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:border-accent"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function SettingsPage() {
  const { user, logout } = useAuth();
  const { prefs, update } = usePreferences();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleChange(changes: Record<string, string>) {
    try {
      await update(changes);
    } catch {
      toast.error('Failed to save setting');
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const data = await apiGet<object>('/settings/export');
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dossier-export-${new Date().toISOString().slice(0, 10)}.dossier`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  }

  async function handleImport(file: File) {
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (data.format !== 'dossier-export') {
        toast.error('Invalid .dossier file');
        return;
      }
      const res = await fetch('/api/settings/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await (await import('../lib/firebase')).getIdToken()}`,
        },
        body: text,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error);
      }
      const result = await res.json();
      const total = Object.values(result.counts as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
      toast.success(`Imported ${total} items`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import data');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      toast.error('Failed to sign out');
      setSigningOut(false);
    }
  }

  function handleClearRecent() {
    localStorage.removeItem('dossier-recent-items');
    toast.success('Recent items cleared');
  }

  function handleClearSearchHistory() {
    localStorage.removeItem('dossier-recent-searches');
    toast.success('Search history cleared');
  }

  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <Settings size={24} className="text-accent" />
          <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        </div>

        {/* Sidebar / Navigation */}
        <Section icon={PanelLeft} title="Sidebar & Navigation">
          <SettingRow label="Show recent items" description="Display recently visited items below the main menu">
            <Toggle
              checked={prefs['sidebar.recentItems'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.recentItems': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Recent items count" description="Number of recent items to show (3-10)">
            <Select
              value={prefs['sidebar.recentItemsCount'] || '5'}
              onChange={v => handleChange({ 'sidebar.recentItemsCount': v })}
              options={[
                { value: '3', label: '3' },
                { value: '5', label: '5' },
                { value: '7', label: '7' },
                { value: '10', label: '10' },
              ]}
            />
          </SettingRow>
          <SettingRow label="Sidebar open by default" description="Start with sidebar expanded on desktop">
            <Toggle
              checked={prefs['sidebar.defaultOpen'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.defaultOpen': v ? 'true' : 'false' })}
            />
          </SettingRow>
        </Section>

        {/* Appearance */}
        <Section icon={Palette} title="Appearance">
          <SettingRow label="Theme" description="Light mode is coming soon">
            <Select
              value={prefs['appearance.theme'] || 'dark'}
              onChange={v => handleChange({ 'appearance.theme': v })}
              options={[
                { value: 'dark', label: 'Dark' },
                { value: 'light', label: 'Light (coming soon)' },
              ]}
            />
          </SettingRow>
        </Section>

        {/* Timezone */}
        <Section icon={Globe} title="Timezone">
          <SettingRow
            label="Timezone"
            description={prefs['timezone'] === 'auto' ? `Auto-detected: ${detectedTimezone}` : undefined}
          >
            <Select
              value={prefs['timezone'] || 'auto'}
              onChange={v => handleChange({ 'timezone': v })}
              options={TIMEZONE_OPTIONS}
            />
          </SettingRow>
        </Section>

        {/* Editor */}
        <Section icon={Type} title="Editor">
          <SettingRow label="Show toolbar by default" description="Display the formatting toolbar when opening an editor">
            <Toggle
              checked={prefs['editor.toolbarVisible'] !== 'false'}
              onChange={v => handleChange({ 'editor.toolbarVisible': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Auto-save delay" description="How long to wait after typing before saving">
            <Select
              value={prefs['editor.autosaveDelay'] || '2000'}
              onChange={v => handleChange({ 'editor.autosaveDelay': v })}
              options={[
                { value: '1000', label: '1 second' },
                { value: '2000', label: '2 seconds' },
                { value: '5000', label: '5 seconds' },
              ]}
            />
          </SettingRow>
        </Section>

        {/* Notifications */}
        <Section icon={Bell} title="Notifications">
          <SettingRow label="Toast duration" description="How long notifications stay visible">
            <Select
              value={prefs['toast.duration'] || '3000'}
              onChange={v => handleChange({ 'toast.duration': v })}
              options={[
                { value: '2000', label: 'Short (2s)' },
                { value: '3000', label: 'Normal (3s)' },
                { value: '5000', label: 'Long (5s)' },
              ]}
            />
          </SettingRow>
          <SettingRow label="Toast position" description="Where notifications appear on screen">
            <Select
              value={prefs['toast.position'] || 'bottom-right'}
              onChange={v => handleChange({ 'toast.position': v })}
              options={[
                { value: 'bottom-right', label: 'Bottom right' },
                { value: 'top-right', label: 'Top right' },
                { value: 'bottom-center', label: 'Bottom center' },
              ]}
            />
          </SettingRow>
        </Section>

        {/* Data */}
        <Section icon={Database} title="Data">
          <SettingRow label="Export data" description="Download all your data as a .dossier file">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent/10 text-accent hover:bg-accent/20 transition-colors disabled:opacity-50"
            >
              <Download size={12} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </SettingRow>
          <SettingRow label="Import data" description="Restore from a .dossier file (merges with existing data)">
            <input
              ref={fileInputRef}
              type="file"
              accept=".dossier"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleImport(file);
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input transition-colors disabled:opacity-50"
            >
              <Upload size={12} />
              {importing ? 'Importing...' : 'Import'}
            </button>
          </SettingRow>
          <SettingRow label="Recent items" description="Clear the recently visited items list in the sidebar">
            <button
              onClick={handleClearRecent}
              className="px-3 py-1.5 text-xs rounded-md bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input transition-colors"
            >
              Clear
            </button>
          </SettingRow>
          <SettingRow label="Search history" description="Clear saved recent searches from the command palette">
            <button
              onClick={handleClearSearchHistory}
              className="px-3 py-1.5 text-xs rounded-md bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input transition-colors"
            >
              Clear
            </button>
          </SettingRow>
        </Section>

        {/* Account */}
        <Section icon={User} title="Account">
          <SettingRow label="Email" description={user?.email || 'Not signed in'}>
            <span className="text-xs text-text-muted">{user?.displayName}</span>
          </SettingRow>
          <SettingRow label="Sign out" description="Sign out of Dossier">
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-danger/10 text-danger hover:bg-danger/20 transition-colors disabled:opacity-50"
            >
              <LogOut size={12} />
              {signingOut ? 'Signing out...' : 'Sign out'}
            </button>
          </SettingRow>
        </Section>
      </div>
    </div>
  );
}
